import { Animal } from 'src/domain/animal/Animal'
import { AnimalHistoricParams } from 'src/domain/animal/AnimalHistoric'
import {
  AnimalDto,
  AnimalHistoricDTO,
  AnimalRecordDto,
  BloodGradeDto,
  CreateVaccineHistoryDto,
  IAnimal,
  ProductiveAnimalDTO,
  UpdateAnimalDto,
  UpdateAnimalImageDto,
  VaccineHistoryAnimalDto
} from 'src/dto/animal.dto'
import { EarringDto } from 'src/dto/birth-certificate.dto'
import { AnimalService } from 'src/services/api/animal.service'

export class AnimalController {
  private service: AnimalService

  constructor() {
    this.service = new AnimalService()
  }

  public async getAnimalListByFarmId(farmId: string, params: any): Promise<AnimalDto> {
    const apiParams = {
      ...params,
      tokenPage: params.tokenPage ? params.tokenPage : null
    }

    const pageParams = await this.getTotalPagesAnimalList(farmId, apiParams)

    const response = await this.service.getAnimalListByFarmId(farmId, apiParams)

    const nextPage = params.tokenPage ? params.tokenPage + 1 : pageParams.totalPages > 1 ? 2 : null
    const list = response.data.filter((animal: IAnimal) => animal.acao !== 'Removido')

    return {
      totalPages: pageParams.totalPages,
      total: pageParams.Total,
      nextPage,
      list
    }
  }

  public async getAnimalById(farmId: string, id: string): Promise<IAnimal> {
    const response = await this.service.getAnimalById(farmId, id)
    return response.data[0]
  }

  public async getAnimalListByLoteId(farmId: string, loteId: string, params?: any): Promise<IAnimal[]> {
    const response = await this.service.getAnimalListByLoteId(farmId, loteId, params)
    return response.data.filter((animal: IAnimal) => animal.acao !== 'Removido')
  }

  public async getAnimalImage(farmId: string, animalId: string): Promise<Blob | null> {
    const response = await this.service.getAnimalImageData(farmId, animalId)
    const imageData = response.data && response.data[0] ? response.data[0] : null

    if (!imageData) {
      return null
    }

    try {
      const imageResponse = await this.service.getAnimalImage(farmId, imageData.idAnimal)
      return imageResponse.data
    } catch (error) {
      console.error('Erro ao buscar imagem do animal:', error)
      return null
    }
  }

  public async updateAnimal(farmId: string, updateAnimalDto: UpdateAnimalDto): Promise<IAnimal> {
    const animalFormatedData = new Animal(updateAnimalDto)

    const response = await this.service.updateAnimal(farmId, animalFormatedData)
    return response.data
  }

  public async updateAnimalImage(updateAnimalImageDto: UpdateAnimalImageDto) {
    const response = await this.service.updateAnimalImage(updateAnimalImageDto)
    return response.data
  }

  public async getVaccineHistoryByAnimal(farmId: string, animalId: string): Promise<VaccineHistoryAnimalDto[]> {
    const response = await this.service.getVaccineHistoryByAnimal(farmId, animalId)
    return response.data
  }

  public async getBloodGradeList(): Promise<BloodGradeDto[]> {
    const response = await this.service.getBloodGradeList()
    const dataOrdered = response.data.sort((a: BloodGradeDto, b: BloodGradeDto) =>
      a.descricao.localeCompare(b.descricao)
    )
    return dataOrdered
  }

  public async getAnimalRecordsList(farmId: string, animalId: string): Promise<AnimalRecordDto[]> {
    const response = await this.service.getAnimalRecordsList(farmId, animalId)
    return response.data
  }

  public async getTotalPagesAnimalList(farmId: string, params?: any): Promise<{ Total: number; totalPages: number }> {
    const response = await this.service.getTotalPagesAnimalList(farmId, params)
    return response.data
  }

  public async getProductiveAnimalList(farmId: string, animalId: string): Promise<ProductiveAnimalDTO[]> {
    const response = await this.service.getProductiveAnimalList(farmId, animalId)
    return response.data
  }

  public async createProductiveAnimal(farmId: string, data?: any) {
    const response = await this.service.createProductiveAnimal(farmId, data)
    return response.data
  }

  public async createAnimalHistoric(farmId: string, data?: AnimalHistoricParams): Promise<AnimalHistoricDTO> {
    const response = await this.service.createAnimalHistoric(farmId, data)
    return response.data
  }

  public async createAnimalVaccineHistoric(farmId: string, data: CreateVaccineHistoryDto): Promise<any> {
    const response = await this.service.createAnimalVaccineHistoric(farmId, data)
    return response.data
  }

  // ========== Métodos de Orquestração (Operações Compostas) ==========

  /**
   * Cria um animal completo com todos os registros subjacentes
   * Método orquestrador que cria animal, registro produtivo e histórico
   *
   * @param farmId - ID da fazenda
   * @param animalData - Dados do animal
   * @returns Animal criado com registros produtivo e histórico
   */
  public async createAnimalComplete(
    farmId: string,
    username: string,
    animalData: UpdateAnimalDto
  ): Promise<{
    animal: IAnimal
    productive: ProductiveAnimalDTO
    historic: AnimalHistoricDTO
  }> {
    return await this.service.createAnimalComplete(farmId, username, animalData)
  }
}
